// src/pages/WorkspaceLOIEditor.jsx
// Parallel to WorkspaceNDAEditor.jsx — same UX, different backend endpoint
// and no requires_* toggles since LOI is per-buyer (not a workspace gate).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { API_BASE, getCsrfToken } from "../services/server";
import "./css/nda-editor.css";

async function apiFetch(path, opts = {}) {
  const csrf = getCsrfToken();
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (csrf) headers["X-CSRFToken"] = csrf;
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...opts,
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  return res;
}

// LOI-specific auto-tags. Mirrors NDA's set with addition of offer_amount
// and exclusivity_days which the default LOI body references.
const AUTO_TAGS = [
  { key: "name",             label: "Undertecknarens namn",   example: "Köparens namn kommer stå här" },
  { key: "date",             label: "Signeringsdatum",        example: "Signeringsdatum fylls i automatiskt" },
  { key: "company",          label: "Köparens bolagsnamn",    example: "Köparens bolag kommer stå här" },
  { key: "workspace_name",   label: "Målbolagets namn",       example: "Målbolagets namn kommer stå här" },
  { key: "offer_amount",     label: "Budbelopp (SEK)",        example: "Budbeloppet fylls i automatiskt från pipelinen" },
  { key: "exclusivity_days", label: "Exklusivitetsdagar",     example: "Exklusivitetsperiod (dagar)" },
];

function renderTagged(text, tagValues) {
  const all = {
    name:             "[Köparens namn kommer stå här]",
    date:             "[Signeringsdatum fylls i automatiskt]",
    company:          "[Köparens bolag kommer stå här]",
    workspace_name:   "[Målbolagets namn kommer stå här]",
    offer_amount:     "[Budbelopp från pipelinen]",
    exclusivity_days: "[Exklusivitetsdagar]",
    ...tagValues,
  };

  return text.split(/(\{\{[^}]+\}\})/g).map((part, i) => {
    const m = part.match(/^\{\{([^}]+)\}\}$/);
    if (!m) return <span key={i} style={{ whiteSpace: "pre-wrap" }}>{part}</span>;
    const key = m[1];
    const val = all[key];
    const isAuto = AUTO_TAGS.some((t) => t.key === key);
    return (
      <span
        key={i}
        title={`Tag: {{${key}}}`}
        style={{
          background: isAuto ? "#E7E5E4" : "#fef9c3",
          color: isAuto ? "#57534E" : "#92400e",
          padding: "0 3px",
          borderRadius: 3,
          fontWeight: 500,
          fontSize: "inherit",
        }}
      >
        {val !== undefined ? val : `{{${key}}}`}
      </span>
    );
  });
}

export default function WorkspaceLOIEditor() {
  const { company: workspaceKey } = useParams();

  const [title, setTitle] = useState("Avsiktsförklaring");
  const [content, setContent] = useState("");
  const [tagValues, setTagValues] = useState({ exclusivity_days: "30" });
  const [logoData, setLogoData] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(false);

  const [newTagKey, setNewTagKey] = useState("");
  const [newTagVal, setNewTagVal] = useState("");

  const textareaRef = useRef();

  useEffect(() => {
    apiFetch(`/users/workspaces/${workspaceKey}/loi/template/`)
      .then(async (res) => {
        if (!res.ok) throw new Error("Kunde inte ladda LOI-mall.");
        return res.json();
      })
      .then((data) => {
        setTitle(data.title || "Avsiktsförklaring");
        setContent(data.content || "");
        setTagValues(data.tag_values || { exclusivity_days: "30" });
        setLogoData(data.logo_data || "");
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [workspaceKey]);

  async function save() {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const res = await apiFetch(`/users/workspaces/${workspaceKey}/loi/template/`, {
        method: "PATCH",
        body: {
          title,
          content,
          tag_values: tagValues,
          logo_data: logoData,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Kunde inte spara.");
      setMessage("Sparat.");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const insertTag = useCallback((key) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const tag = `{{${key}}}`;
    const next = content.slice(0, start) + tag + content.slice(end);
    setContent(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + tag.length;
    });
  }, [content]);

  function handleLogoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLogoData(ev.target.result);
    reader.readAsDataURL(file);
  }

  function addTag() {
    const k = newTagKey.trim().replace(/\s+/g, "_").toLowerCase();
    const v = newTagVal.trim();
    if (!k) return;
    setTagValues((prev) => ({ ...prev, [k]: v }));
    setNewTagKey("");
    setNewTagVal("");
  }

  function removeTag(key) {
    setTagValues((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  }

  if (loading) {
    return (
      <div className="nda-shell">
        <div className="nda-loading">Laddar…</div>
      </div>
    );
  }

  return (
    <div className="nda-shell">
      <header className="nda-topbar">
        <div className="nda-topbar-left">
          <Link to={`/settings/${workspaceKey}`} className="nda-back">
            ← Inställningar
          </Link>
          <span className="nda-topbar-sep">›</span>
          <span className="nda-topbar-title">LOI-mall</span>
        </div>
        <div className="nda-topbar-right">
          <button
            type="button"
            className={`nda-btn nda-btn--ghost${preview ? " nda-btn--active" : ""}`}
            onClick={() => setPreview((v) => !v)}
          >
            {preview ? "Redigera" : "Förhandsgranskning"}
          </button>
          <button
            type="button"
            className="nda-btn nda-btn--primary"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Sparar…" : "Spara"}
          </button>
        </div>
      </header>

      <div style={legalDisclaimerBannerStyle}>
        <span aria-hidden="true">⚠</span>
        <span>Mall — inte juridisk rådgivning. Låt jurist granska innan undertecknande.</span>
      </div>

      {error && <div className="nda-banner nda-banner--error">{error}</div>}
      {message && <div className="nda-banner nda-banner--success">{message}</div>}

      <div className="nda-body">
        <div className="nda-editor-col">
          <div className="nda-field">
            <label className="nda-label">Dokumenttitel</label>
            <input
              className="nda-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Avsiktsförklaring"
            />
          </div>

          {!preview && (
            <div className="nda-tag-toolbar">
              <span className="nda-tag-toolbar-label">Infoga tagg:</span>
              {AUTO_TAGS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className="nda-tag-btn nda-tag-btn--auto"
                  onClick={() => insertTag(t.key)}
                  title={t.label}
                >
                  {`{{${t.key}}}`}
                </button>
              ))}
              {Object.keys(tagValues).map((k) => (
                <button
                  key={k}
                  type="button"
                  className="nda-tag-btn nda-tag-btn--custom"
                  onClick={() => insertTag(k)}
                  title={`Anpassad tagg — värde: ${tagValues[k]}`}
                >
                  {`{{${k}}}`}
                </button>
              ))}
            </div>
          )}

          {preview ? (
            <div className="nda-preview-doc">
              {logoData && (
                <img src={logoData} alt="Logo" className="nda-preview-logo" />
              )}
              <h1 className="nda-preview-title">{title}</h1>
              <div className="nda-preview-content">
                {renderTagged(content, tagValues)}
              </div>
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              className="nda-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              spellCheck={false}
              placeholder="Skriv eller klistra in LOI-texten här. Använd {{tagg}} för platshållare."
            />
          )}
        </div>

        <aside className="nda-settings-col">
          <div className="nda-panel-section">
            <div className="nda-panel-title">Logo</div>
            {logoData && (
              <div className="nda-logo-preview-wrap">
                <img src={logoData} alt="Logo" className="nda-logo-preview" />
                <button
                  type="button"
                  className="nda-logo-remove"
                  onClick={() => setLogoData("")}
                >
                  Ta bort
                </button>
              </div>
            )}
            <label className="nda-upload-btn">
              {logoData ? "Byt logo" : "Ladda upp logo"}
              <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: "none" }} />
            </label>
          </div>

          <div className="nda-panel-section">
            <div className="nda-panel-title">Anpassade taggar</div>
            <p className="nda-panel-desc">
              Definiera egna taggar med fasta värden som ersätts i dokumentet.
            </p>

            {Object.entries(tagValues).map(([k, v]) => (
              <div key={k} className="nda-custom-tag-row">
                <code className="nda-tag-code">{`{{${k}}}`}</code>
                <input
                  className="nda-tag-value-input"
                  value={v}
                  onChange={(e) =>
                    setTagValues((prev) => ({ ...prev, [k]: e.target.value }))
                  }
                  placeholder="Värde…"
                />
                <button
                  type="button"
                  className="nda-tag-remove"
                  onClick={() => removeTag(k)}
                  title="Ta bort"
                >
                  ×
                </button>
              </div>
            ))}

            <div className="nda-add-tag-row">
              <input
                className="nda-tag-key-input"
                placeholder="taggnamn"
                value={newTagKey}
                onChange={(e) => setNewTagKey(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTag()}
              />
              <input
                className="nda-tag-value-input"
                placeholder="värde"
                value={newTagVal}
                onChange={(e) => setNewTagVal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTag()}
              />
              <button
                type="button"
                className="nda-btn nda-btn--sm"
                onClick={addTag}
                disabled={!newTagKey.trim()}
              >
                + Lägg till
              </button>
            </div>
          </div>

          <div className="nda-panel-section">
            <div className="nda-panel-title">Standardtaggar</div>
            <p className="nda-panel-desc">Löses automatiskt vid signering.</p>
            <div className="nda-std-tags">
              {AUTO_TAGS.map((t) => (
                <div key={t.key} className="nda-std-tag-row">
                  <code className="nda-tag-code">{`{{${t.key}}}`}</code>
                  <span className="nda-std-tag-label">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

const legalDisclaimerBannerStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 16px",
  background: "#FEF3C7",
  borderBottom: "1px solid #FBBF24",
  color: "#78350F",
  fontSize: 13,
  fontWeight: 500,
};
